import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { Paperclip, Upload, X, Trash2, Loader2, ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles, Tag, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RAW_BYTES = 20 * 1024 * 1024; // 20MB
const MAX_PER_PARENT = 5;
const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/**
 * Reusable attachment widget. Lazy-mount friendly: nothing loads until `enabled` is true.
 *
 * Props:
 *   parentType: 'appointment' | 'symptom' | 'flare_journal' | 'lab_result' | 'vaccine'
 *   parentId:   string - parent record id
 *   enabled:    boolean - whether to actually fetch (lazy-mount control). Default true.
 *   compact:    boolean - smaller layout for inline use
 *   onCountChange: (n: number) => void - called when attachment count changes
 */
export default function AttachmentSection({ parentType, parentId, enabled = true, compact = false, onCountChange }) {
    const { token } = useAuth();
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [blobUrls, setBlobUrls] = useState({});
    const [tagSuggestions, setTagSuggestions] = useState({}); // {att_id: [tags]}
    const [suggesting, setSuggesting] = useState(false);
    const fileInputRef = useRef(null);

    const reachedLimit = attachments.length >= MAX_PER_PARENT;

    // ---------- Load existing attachments ----------
    const load = useCallback(async () => {
        if (!enabled || !parentId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/attachments`, {
                params: { parent_type: parentType, parent_id: parentId },
            });
            setAttachments(data || []);
            if (onCountChange) onCountChange((data || []).length);
        } catch (e) {
            console.error('Failed to load attachments', e);
        } finally {
            setLoading(false);
        }
    }, [enabled, parentId, parentType, onCountChange]);

    useEffect(() => { load(); }, [load]);

    // ---------- Build blob URLs (auth-protected images) ----------
    useEffect(() => {
        if (!enabled || attachments.length === 0) return;
        let cancelled = false;
        const newUrls = {};
        (async () => {
            for (const a of attachments) {
                if (blobUrls[a.id]) {
                    newUrls[a.id] = blobUrls[a.id];
                    continue;
                }
                try {
                    const resp = await axios.get(`${API_URL}/api/attachments/${a.id}/download`, {
                        responseType: 'blob',
                    });
                    if (cancelled) return;
                    newUrls[a.id] = URL.createObjectURL(resp.data);
                } catch (e) {
                    console.error('Failed to load attachment blob', a.id, e);
                }
            }
            if (!cancelled) setBlobUrls(newUrls);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachments, enabled]);

    // Clean up blob URLs on unmount
    useEffect(() => () => {
        Object.values(blobUrls).forEach(u => URL.revokeObjectURL(u));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- HEIC conversion + compression ----------
    const prepareFile = async (file) => {
        let working = file;
        const name = (file.name || '').toLowerCase();
        const mime = (file.type || '').toLowerCase();
        const isHeic = mime === 'image/heic' || mime === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');

        if (isHeic) {
            const heic2any = (await import('heic2any')).default;
            const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
            const out = Array.isArray(blob) ? blob[0] : blob;
            working = new File([out], (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        }

        // Validate after conversion
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(working.type)) {
            throw new Error('Unsupported file type. Use JPG, PNG, WEBP, or HEIC.');
        }

        // Compress to ~2MB / 2048px
        try {
            const compressed = await imageCompression(working, {
                maxSizeMB: 2,
                maxWidthOrHeight: 2048,
                useWebWorker: true,
                fileType: working.type,
            });
            return compressed;
        } catch (e) {
            console.warn('Compression failed, using original', e);
            return working;
        }
    };

    // ---------- Handle file selection ----------
    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        const remaining = MAX_PER_PARENT - attachments.length;
        if (remaining <= 0) {
            toast.error(`Maximum ${MAX_PER_PARENT} attachments per record`);
            return;
        }

        const toUpload = files.slice(0, remaining);
        if (files.length > remaining) {
            toast.warning(`Only first ${remaining} file(s) uploaded — ${MAX_PER_PARENT}-attachment cap.`);
        }

        setUploading(true);
        let success = 0;

        for (const f of toUpload) {
            // Pre-check raw size BEFORE compression so user gets feedback
            if (f.size > MAX_RAW_BYTES) {
                toast.error(`${f.name}: exceeds 20MB limit`);
                continue;
            }
            const ext = (f.name || '').toLowerCase();
            const looksAllowed = ACCEPTED_MIME.includes(f.type) ||
                /\.(jpe?g|png|webp|heic|heif)$/i.test(ext);
            if (!looksAllowed) {
                toast.error(`${f.name}: unsupported file type`);
                continue;
            }

            try {
                const prepared = await prepareFile(f);
                const form = new FormData();
                form.append('file', prepared, prepared.name || f.name);
                form.append('parent_type', parentType);
                form.append('parent_id', parentId);

                await axios.post(`${API_URL}/api/attachments/upload`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                success++;
            } catch (e) {
                const detail = e?.response?.data?.detail || e.message || 'Upload failed';
                toast.error(`${f.name}: ${detail}`);
            }
        }

        if (success > 0) toast.success(`Uploaded ${success} photo${success === 1 ? '' : 's'}`);
        setUploading(false);
        await load();
    };

    const onInputChange = (e) => {
        handleFiles(e.target.files);
        e.target.value = '';
    };

    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const onDragLeave = () => setIsDragging(false);
    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    // ---------- Delete ----------
    const doDelete = async (id) => {
        try {
            await axios.delete(`${API_URL}/api/attachments/${id}`);
            toast.success('Photo deleted');
            setConfirmDelete(null);
            // Revoke its blob url
            if (blobUrls[id]) URL.revokeObjectURL(blobUrls[id]);
            setBlobUrls(prev => {
                const n = { ...prev };
                delete n[id];
                return n;
            });
            await load();
        } catch (e) {
            toast.error('Failed to delete');
        }
    };

    // ---------- AI tag suggest / accept ----------
    const requestSuggestions = async (attId) => {
        setSuggesting(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/attachments/${attId}/ai-suggest-tags`);
            setTagSuggestions(prev => ({ ...prev, [attId]: data.suggestions || [] }));
            if (!data.suggestions || data.suggestions.length === 0) {
                toast.info('No clear tags suggested for this image');
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || 'AI tagging failed';
            toast.error(detail);
        } finally {
            setSuggesting(false);
        }
    };

    const acceptTag = async (att, newTag) => {
        const current = att.tags || [];
        if (current.includes(newTag)) return;
        const next = [...current, newTag];
        try {
            const { data } = await axios.post(`${API_URL}/api/attachments/${att.id}/tags`, { tags: next });
            // Reflect locally
            setAttachments(prev => prev.map(p => p.id === att.id ? { ...p, tags: data.tags } : p));
            // Remove from suggestions
            setTagSuggestions(prev => ({
                ...prev,
                [att.id]: (prev[att.id] || []).filter(t => t !== newTag),
            }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save tag');
        }
    };

    const removeTag = async (att, tag) => {
        const next = (att.tags || []).filter(t => t !== tag);
        try {
            const { data } = await axios.post(`${API_URL}/api/attachments/${att.id}/tags`, { tags: next });
            setAttachments(prev => prev.map(p => p.id === att.id ? { ...p, tags: data.tags } : p));
        } catch (e) {
            toast.error('Failed to remove tag');
        }
    };

    // ---------- Lightbox keyboard nav ----------
    useEffect(() => {
        if (lightboxIndex === null) return;
        const handler = (e) => {
            if (e.key === 'Escape') setLightboxIndex(null);
            if (e.key === 'ArrowLeft') setLightboxIndex(i => (i === null ? null : Math.max(0, i - 1)));
            if (e.key === 'ArrowRight') setLightboxIndex(i => (i === null ? null : Math.min(attachments.length - 1, i + 1)));
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [lightboxIndex, attachments.length]);

    if (!enabled) return null;

    return (
        <div className="space-y-3" data-testid={`attachments-${parentType}-${parentId}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    <Paperclip className="w-4 h-4" />
                    <span>Photos & Attachments</span>
                    <span className="text-xs text-slate-500">({attachments.length}/{MAX_PER_PARENT})</span>
                </div>
                {!reachedLimit && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="attachment-add-btn"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        {uploading ? 'Uploading…' : 'Add'}
                    </Button>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                    multiple
                    onChange={onInputChange}
                    className="hidden"
                    data-testid="attachment-file-input"
                />
            </div>

            {/* Drag-drop / thumbnail area */}
            <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`rounded-lg border-2 border-dashed transition-colors ${
                    isDragging
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                        : 'border-slate-300 dark:border-slate-700'
                } ${compact ? 'p-2' : 'p-3'}`}
            >
                {loading ? (
                    <div className="flex items-center justify-center py-6 text-slate-500">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                    </div>
                ) : attachments.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 dark:text-slate-400">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Drag & drop photos here, or click <strong>Add</strong></p>
                        <p className="text-xs mt-1">JPG, PNG, WEBP, HEIC • max 20MB • up to {MAX_PER_PARENT}</p>
                    </div>
                ) : (
                    <div className={`grid gap-2 ${compact ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-3 sm:grid-cols-5'}`}>
                        {attachments.map((a, idx) => (
                            <div
                                key={a.id}
                                className="relative group aspect-square rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 cursor-pointer"
                                onClick={() => setLightboxIndex(idx)}
                                data-testid={`attachment-thumb-${idx}`}
                            >
                                {blobUrls[a.id] ? (
                                    <img
                                        src={blobUrls[a.id]}
                                        alt={a.original_filename}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(a.id); }}
                                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`attachment-delete-${idx}`}
                                    aria-label="Delete photo"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxIndex !== null && attachments[lightboxIndex] && (
                <div
                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setLightboxIndex(null)}
                    data-testid="attachment-lightbox"
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
                        className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                        aria-label="Close"
                        data-testid="lightbox-close"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    {lightboxIndex > 0 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
                            className="absolute left-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                            aria-label="Previous"
                            data-testid="lightbox-prev"
                        >
                            <ChevronLeft className="w-7 h-7" />
                        </button>
                    )}
                    {lightboxIndex < attachments.length - 1 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
                            className="absolute right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                            aria-label="Next"
                            data-testid="lightbox-next"
                        >
                            <ChevronRight className="w-7 h-7" />
                        </button>
                    )}
                    <div className="flex flex-col items-center gap-3 max-w-[95vw] max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={blobUrls[attachments[lightboxIndex].id]}
                            alt={attachments[lightboxIndex].original_filename}
                            className="max-w-full max-h-[60vh] object-contain"
                        />
                        <div className="text-white text-xs bg-black/60 px-3 py-1 rounded-full">
                            {lightboxIndex + 1} / {attachments.length} · {attachments[lightboxIndex].original_filename}
                        </div>

                        {/* Tag panel */}
                        {(() => {
                            const att = attachments[lightboxIndex];
                            const currentTags = att.tags || [];
                            const suggestions = (tagSuggestions[att.id] || []).filter(s => !currentTags.includes(s));
                            return (
                                <div className="w-full max-w-xl bg-slate-900/90 backdrop-blur rounded-lg p-3 space-y-2" data-testid="lightbox-tag-panel">
                                    <div className="flex items-center gap-2 text-xs text-slate-300">
                                        <Tag className="w-3.5 h-3.5" /> Tags
                                    </div>
                                    {/* Current tags */}
                                    <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                                        {currentTags.length === 0 && (
                                            <span className="text-xs text-slate-500 italic">No tags yet</span>
                                        )}
                                        {currentTags.map(t => (
                                            <span
                                                key={t}
                                                className="inline-flex items-center gap-1 bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full"
                                                data-testid={`accepted-tag-${t}`}
                                            >
                                                {t}
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(att, t)}
                                                    className="hover:bg-indigo-600 rounded-full p-0.5"
                                                    aria-label={`Remove ${t}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    {/* Suggestions */}
                                    {suggestions.length > 0 && (
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wide text-violet-300 mb-1">Suggested — tap to add</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {suggestions.map(s => (
                                                    <button
                                                        key={s}
                                                        type="button"
                                                        onClick={() => acceptTag(att, s)}
                                                        className="inline-flex items-center gap-1 bg-violet-500/30 hover:bg-violet-500/60 text-violet-100 text-xs px-2 py-0.5 rounded-full border border-violet-400/50"
                                                        data-testid={`suggestion-tag-${s}`}
                                                    >
                                                        <Plus className="w-3 h-3" /> {s}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* Suggest button */}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => requestSuggestions(att.id)}
                                        disabled={suggesting}
                                        className="bg-violet-500/20 border-violet-400/50 hover:bg-violet-500/40 text-violet-100"
                                        data-testid="lightbox-suggest-tags-btn"
                                    >
                                        {suggesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                                        {suggesting ? 'Analyzing…' : (suggestions.length > 0 ? 'Suggest more' : 'Suggest tags (AI)')}
                                    </Button>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            {confirmDelete && (
                <div
                    className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
                    onClick={() => setConfirmDelete(null)}
                    data-testid="attachment-delete-confirm"
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-sm w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100">Delete photo?</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">This action cannot be undone.</p>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="delete-cancel">Cancel</Button>
                            <Button variant="destructive" onClick={() => doDelete(confirmDelete)} data-testid="delete-confirm">Delete</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
